// features/orders/components/QuickPayDrawer.jsx
import React, { useState } from 'react'
import { Drawer } from '../../../components/ui/Drawer'
import { Button } from '../../../components/ui/Button'
import { Input, Textarea } from '../../../components/ui/Input'
import { useAutoSave } from '../../../hooks/useAutoSave'
import { toast, toastSuccess, toastError } from '../../../components/feedback/Toast'
import { apiFetch } from '../../../api'
import { formatMoney } from '../../../lib/format'
import { cn } from '../../../lib/cn'
import { DollarSign, CreditCard, Smartphone, CheckCircle } from 'lucide-react'

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Espèces', icon: DollarSign },
  { value: 'card', label: 'Carte', icon: CreditCard },
  { value: 'mobile', label: 'Mobile Money', icon: Smartphone },
]

export function QuickPayDrawer({ order, open, onClose, onSuccess }) {
  const [amount, setAmount] = useState(order?.remaining || 0)
  const [method, setMethod] = useState('cash')
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(false)

  // Auto-save note (debounced)
  const { status: saveStatus } = useAutoSave(
    note,
    async (value) => {
      if (value.trim()) {
        await apiFetch(`/api/orders/${order.id}/notes`, {
          method: 'POST',
          body: { note: value },
        })
      }
    },
    1500
  )

  async function handleSubmit(e) {
    e.preventDefault()

    if (amount <= 0) {
      toastError('Montant invalide', 'Le montant doit être supérieur à 0')
      return
    }

    setLoading(true)

    try {
      await apiFetch(`/api/payments/orders/${order.id}/pay`, {
        method: 'POST',
        body: { amount, method, note },
      })

      toastSuccess('Encaissement réussi !', `${formatMoney(amount)} encaissés`)

      onSuccess?.()
      onClose()
    } catch (err) {
      toastError('Erreur encaissement', err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Encaisser un paiement" size="md">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Infos vente */}
        <div className="rounded-lg bg-slate-50 p-4 border border-slate-200">
          <div className="text-xs font-semibold text-slate-500 uppercase">Vente</div>
          <div className="text-lg font-bold text-slate-900 mt-1">
            {order?.external_id || order?.id}
          </div>
          <div className="text-sm text-slate-600 mt-1">
            Reste à payer : <span className="font-bold">{formatMoney(order?.remaining || 0)}</span>
          </div>
        </div>

        {/* Montant */}
        <Input
          label="Montant à encaisser"
          type="number"
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
          leftIcon={DollarSign}
          autoFocus
        />

        <button
          type="button"
          onClick={() => setAmount(order?.remaining || 0)}
          className="text-sm text-purple-600 hover:text-purple-700 font-medium"
        >
          → Solde complet ({formatMoney(order?.remaining || 0)})
        </button>

        {/* Moyen de paiement */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-3">
            Moyen de paiement
          </label>
          <div className="grid grid-cols-3 gap-2">
            {PAYMENT_METHODS.map((m) => {
              const Icon = m.icon
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMethod(m.value)}
                  className={cn(
                    'flex flex-col items-center gap-2 p-3 rounded-lg border transition',
                    method === m.value
                      ? 'border-purple-500 bg-purple-50 text-purple-700 ring-2 ring-purple-100'
                      : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                  )}
                >
                  <Icon className="h-6 w-6" />
                  <span className="text-xs font-medium">{m.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Note (auto-save) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-slate-700">
              Note <span className="text-xs text-slate-500 font-normal">(optionnel)</span>
            </label>
            {saveStatus === 'saving' && (
              <span className="text-xs text-slate-500">Sauvegarde...</span>
            )}
            {saveStatus === 'saved' && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Enregistré
              </span>
            )}
          </div>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Remarque sur ce paiement..."
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-slate-200">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
            Annuler
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={amount <= 0}
            className="flex-1"
          >
            Encaisser {formatMoney(amount)}
          </Button>
        </div>
      </form>
    </Drawer>
  )
}
